import React, { createContext, useState, useEffect, ReactNode, FC, useCallback, useMemo, useRef } from 'react'
import { AppSpin } from '@/shared/ui'
import * as Sentry from '@sentry/react'
import { getAuthAdapter, IAuthAdapter, logoutProps } from './providers'
import { getImage, profile } from '../Main/Services/User/api'
import {
  AccessControl,
  prefetchModules
} from '../refactory/modules/access-control/infrastructure/roles-permissions/access-control'
import { RolesPermissionsGlobalState } from '../refactory/modules/access-control/infrastructure/roles-permissions/roles-permissions-global-state'
import { useHookstate } from '@hookstate/core'
import Session from '../Main/Services/User/session'
import { errorsAuthUser, KEY_LOCAL_STORAGE_USER_INFO } from './Auth.constants'
import { firebaseSignInWithCustomToken, firebaseSignOut } from '../refactory/services/firebase/firebase'
import { ContainerSpin } from './Auth.styles'
import { getAuthProvider } from './providers'
import history from '../utils/history'
import { getErrorMessage } from '../refactory/utils/transform'
import { showAlert } from '../Main/Libs/alerts'
import { useTranslation } from 'react-i18next'
import { useRedirectUrl } from './hooks/useRedirectUrl'
import { setRootColorPalette } from '../refactory/kernel/styles-intercept'
import { clarityIdentify, claritySet } from '../shared/services/clarity'

export interface AuthContextProps {
  loading: boolean
  loginError: boolean
  userInfo: UserInfo | null
  isAuthenticated: boolean
  logout: (props?: logoutProps) => Promise<void>
  updateUserInfo: (user: UserInfo | null | ((prev: UserInfo | null) => UserInfo | null)) => void
  getUserProfile: () => Promise<void>
  refreshFirebaseSession: () => Promise<void>
}
export const AuthContext = createContext<AuthContextProps | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [loginError, setLoginError] = useState(false)
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null)
  const userInfoRef = useRef(userInfo)
  userInfoRef.current = userInfo
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const { permissions } = useHookstate(RolesPermissionsGlobalState)
  const [authProvider] = useState(() => getAuthProvider())
  const authAdapter: IAuthAdapter = getAuthAdapter(authProvider)
  const { saveRedirectUrl, getRedirectUrl, clearRedirectUrl } = useRedirectUrl()

  useEffect(() => {
    const currentUrl = `${window.location.pathname}${window.location.search}`
    if (!isAuthenticated && currentUrl !== '/') {
      saveRedirectUrl(currentUrl)
    }
  }, [saveRedirectUrl, isAuthenticated])

  useEffect(() => {
    const initializeAuth = async () => {
      setLoginError(false)
      setLoading(true)
      try {
        await authAdapter.init()
        const authenticated = await authAdapter.isAuthenticated()
        setIsAuthenticated(authenticated)

        if (authenticated) {
          await authAdapter.generateToken()
          prefetchModules()
          void import('@/refactory/modules/jobs/infrastructure/jobs-prefetch').then(({ prefetchJobsFromUrl }) =>
            prefetchJobsFromUrl()
          )
          await getUserProfile()
        } else {
          await authAdapter.redirectLogin()
        }
      } catch (error) {
        console.error('Error during authentication initialization:', error)
        try {
          Sentry.captureException(error, {
            extra: {
              provider: authProvider,
              timestamp: new Date().toISOString(),
              userAgent: navigator.userAgent
            },
            tags: {
              context: 'auth_initialization'
            }
          })
        } catch (sentryError) {
          console.warn('Failed to send error to Sentry:', sentryError)
        }

        setLoginError(true)
        const errorString = getErrorMessage(error)
        const foundError = errorsAuthUser.find((errorItem) => errorString.includes(errorItem))
        if (foundError) {
          const messaege = t(`errors-auth-user.${foundError}`)
          showAlert(messaege, 'error', () => logout())
        } else {
          logout()
        }
      } finally {
        setLoading(false)
      }
    }

    initializeAuth()
  }, [])

  const updateUserInfo = useCallback(
    (userInfoOrUpdater: UserInfo | null | ((prev: UserInfo | null) => UserInfo | null)) => {
      setUserInfo((prev) => {
        const next = typeof userInfoOrUpdater === 'function' ? userInfoOrUpdater(prev) : userInfoOrUpdater
        Session.set(KEY_LOCAL_STORAGE_USER_INFO, next)

        if (next?.user) {
          Sentry.setUser({
            id: next.user.email,
            username: `${next.user.firstName} ${next.user.lastName}`,
            email: next.user.email
          })
          Sentry.setTag('avt_role_id', next.user.avt_role_id)
          Sentry.setTag('avt_role_name', next.user.avt_role?.name)
          Sentry.setTag('avt_role_super_admin', next.user.avt_role?.super_admin)
          Sentry.setTag('client_number', next.client?.clientNumber)
          clarityIdentify(String(next.user.id))
          claritySet('environment', import.meta.env.VITE_ENV)
          claritySet('user_id', String(next.user.id))
          claritySet('client', next.client?.clientNumber)
          claritySet('avt_role_id', next.user.avt_role_id)
        }

        return next
      })
    },
    []
  )

  const getUserProfile = useCallback(async () => {
    try {
      const response: ProfileResponse = await profile()
      const { user } = response

      const [abilityResult] = await Promise.allSettled([
        AccessControl.defineAbilityForRole(user),
        firebaseSignInWithCustomToken(user.firebaseToken, () => {})
      ])
      if (abilityResult.status === 'rejected') {
        await firebaseSignOut()
        throw abilityResult.reason
      }
      permissions.set(abilityResult.value.permissions)
      if (user.client.styles) {
        setRootColorPalette(user.client.styles)
      }

      const previous = userInfoRef.current ?? (Session.get(KEY_LOCAL_STORAGE_USER_INFO) as UserInfo | null)
      const reusableLogo =
        previous?.clientLogoURL &&
        previous.client.id === user.client.id &&
        previous.client.logoUrl === user.client.logoUrl
          ? previous.clientLogoURL
          : ''
      updateUserInfo({ user, client: user.client, clientLogoURL: reusableLogo, isSSO: false })

      const loadClientLogo = async () => {
        try {
          const clientLogoBlob = await getImage(user.client.logoUrl)
          const clientLogoURL = await new Promise<string>((resolve) => {
            const reader = new FileReader()
            reader.onloadend = () => resolve(reader.result as string)
            reader.readAsDataURL(clientLogoBlob)
          })
          updateUserInfo((prev) =>
            prev &&
            prev.user.id === user.id &&
            prev.client.id === user.client.id &&
            prev.client.logoUrl === user.client.logoUrl
              ? { ...prev, clientLogoURL }
              : prev
          )
        } catch (error) {
          console.error('Getting client logo blob', error)
        }
      }
      if (!reusableLogo) void loadClientLogo()

      if (user.is_password_reset_required) {
        history.push('/change-password')
        return
      }

      const redirectUrl = getRedirectUrl()
      clearRedirectUrl()
      history.push(redirectUrl)
    } catch (error) {
      console.error('Error getting user profile:', error)
      throw error
    }
  }, [updateUserInfo, getRedirectUrl, clearRedirectUrl])

  const refreshFirebaseSession = useCallback(async () => {
    const response: ProfileResponse = await profile()
    const { user } = response
    await firebaseSignInWithCustomToken(user.firebaseToken, () => {})
  }, [])

  const logout = useCallback(
    async (props?: logoutProps) => {
      setLoading(true)
      updateUserInfo(null)
      setIsAuthenticated(false)
      Session.remove()
      await authAdapter.logout(props)
      history.push('/')
      setLoading(false)
    },
    [authAdapter, updateUserInfo]
  )

  const contextValue: AuthContextProps = useMemo(
    () => ({
      isAuthenticated,
      userInfo,
      loading,
      loginError,
      logout,
      updateUserInfo,
      getUserProfile,
      refreshFirebaseSession
    }),
    [isAuthenticated, userInfo, loading, loginError, logout, updateUserInfo, getUserProfile, refreshFirebaseSession]
  )

  return loading ? (
    <ContainerSpin>
      <AppSpin size='large' />
    </ContainerSpin>
  ) : isAuthenticated ? (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  ) : (
    <AuthContext.Provider value={contextValue}>{authAdapter.getRenderLogin()}</AuthContext.Provider>
  )
}

export interface UserInfo extends ProfileResponse {
  client: Client
  clientLogoURL: string
  isSSO: boolean
}

export interface ProfileResponse {
  user: User
}

export interface User {
  id: number
  firstName: string
  lastName: string
  email: string
  photoUrl: null
  isLive: boolean
  workload: number
  lastAssignedFlag: null
  createdAt: Date
  updatedAt: Date
  avt_role_id: string
  deletedAt: null
  clientId: number
  roleId: number
  role: Role
  avt_role: AvtRole
  client: Client
  groups: any[]
  firebaseToken: string
  is_password_reset_required: boolean
  stytch_user_id: string | null
}

export interface AvtRole {
  name: string
  super_admin: boolean
  hierarchy: number
}

export interface Client {
  id: number
  name: string
  logoUrl: string
  clientNumber: string
  mainCity: string
  styles: string
  createdAt: Date
  updatedAt: Date
  cluster: string
  timeZone: string
}

export interface Role {
  name: string
}
